import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb } from '../components/Breadcrumb';
import { Card, DashboardLayout } from '../components';
import { adminApi, type AdyenWebhookLog } from '../services/api';

type SortKey = 'createdAt' | 'isAnomaly';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'logged' | 'open' | 'handled';

const KIND_LABEL_KEY: Record<NonNullable<AdyenWebhookLog['anomalyKind']>, string> = {
  unknown_transfer_webhook: 'adminAdyenWebhookLogs.kind.unknown_transfer_webhook',
  unknown_configuration_webhook: 'adminAdyenWebhookLogs.kind.unknown_configuration_webhook',
  reversed_transfer: 'adminAdyenWebhookLogs.kind.reversed_transfer',
  report_import_failed: 'adminAdyenWebhookLogs.kind.report_import_failed',
  validation_error: 'adminAdyenWebhookLogs.kind.validation_error'
};

const KIND_BADGE: Record<NonNullable<AdyenWebhookLog['anomalyKind']>, string> = {
  unknown_transfer_webhook: 'bg-warning text-dark',
  unknown_configuration_webhook: 'bg-info text-dark',
  reversed_transfer: 'bg-danger',
  report_import_failed: 'bg-danger',
  validation_error: 'bg-secondary'
};

function prettyPayload(payload: Record<string, unknown>) {
  return JSON.stringify(payload ?? {}, null, 2);
}

function payloadPreview(payload: Record<string, unknown>, length = 80) {
  const text = prettyPayload(payload).replace(/\s+/g, ' ').trim();
  return text.length > length ? `${text.slice(0, length)}…` : text;
}

export function AdminAdyenWebhookLogs() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AdyenWebhookLog[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [loading, setLoading] = useState(true);
  const [handlingId, setHandlingId] = useState<string | null>(null);
  const [payloadModalRow, setPayloadModalRow] = useState<AdyenWebhookLog | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi
      .getAdyenWebhookLogs(status === 'all' ? undefined : status)
      .then(setRows)
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      anomalies: rows.filter(row => row.isAnomaly).length,
      validation: rows.filter(row => row.anomalyKind === 'validation_error').length,
      reversed: rows.filter(row => row.anomalyKind === 'reversed_transfer').length
    };
  }, [rows]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const multiplier = sortDirection === 'asc' ? 1 : -1;
      if (sortKey === 'isAnomaly') {
        const anomalyCompare = Number(a.isAnomaly) - Number(b.isAnomaly);
        if (anomalyCompare !== 0) return anomalyCompare * multiplier;
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * -1;
      }
      return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * multiplier;
    });
  }, [rows, sortDirection, sortKey]);

  const toggleSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === 'createdAt' ? 'desc' : 'desc');
  };

  const markHandled = async (row: AdyenWebhookLog) => {
    setHandlingId(row.id);
    try {
      await adminApi.handleAdyenWebhookLog(row.id, t('adminAdyenWebhookLogs.handledNote'));
      load();
    } finally {
      setHandlingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminAdyenWebhookLogs.title')}</h5>
          </div>
          <Breadcrumb items={[{ label: t('nav.home'), path: '/admin' }, { label: t('adminAdyenWebhookLogs.title') }]} />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenWebhookLogs.stats.shown')}</small>
            <div className="fw-bold fs-4 text-primary">{stats.total}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenWebhookLogs.stats.anomalies')}</small>
            <div className="fw-bold fs-4 text-danger">{stats.anomalies}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="border rounded p-3 h-100">
            <small className="text-muted">{t('adminAdyenWebhookLogs.stats.reversed')}</small>
            <div className="fw-bold fs-4 text-danger">{stats.reversed}</div>
          </div>
        </div>
      </div>

      <Card>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <select
            className="form-select form-select-sm"
            value={status}
            onChange={event => setStatus(event.target.value as StatusFilter)}
            style={{ maxWidth: 180 }}
          >
            <option value="all">{t('adminAdyenWebhookLogs.filter.all')}</option>
            <option value="logged">{t('adminAdyenWebhookLogs.filter.logged')}</option>
            <option value="open">{t('adminAdyenWebhookLogs.filter.open')}</option>
            <option value="handled">{t('adminAdyenWebhookLogs.filter.handled')}</option>
          </select>
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={load} disabled={loading}>
            <i className="feather-refresh-cw me-1"></i>
            {t('adminAdyenWebhookLogs.refresh')}
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>{t('adminAdyenWebhookLogs.columns.status')}</th>
                <th>
                  <button className="btn btn-link btn-sm p-0 text-decoration-none" type="button" onClick={() => toggleSort('isAnomaly')}>
                    {t('adminAdyenWebhookLogs.columns.anomaly')} {sortKey === 'isAnomaly' ? (sortDirection === 'desc' ? '↓' : '↑') : ''}
                  </button>
                </th>
                <th>{t('adminAdyenWebhookLogs.columns.payloadType')}</th>
                <th>{t('adminAdyenWebhookLogs.columns.reference')}</th>
                <th>{t('adminAdyenWebhookLogs.columns.requestId')}</th>
                <th>
                  <button className="btn btn-link btn-sm p-0 text-decoration-none" type="button" onClick={() => toggleSort('createdAt')}>
                    {t('adminAdyenWebhookLogs.columns.created')} {sortKey === 'createdAt' ? (sortDirection === 'desc' ? '↓' : '↑') : ''}
                  </button>
                </th>
                <th className="text-end">{t('adminAdyenWebhookLogs.columns.action')}</th>
                <th>{t('adminAdyenWebhookLogs.columns.body')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-primary" />
                  </td>
                </tr>
              )}
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    {t('adminAdyenWebhookLogs.empty')}
                  </td>
                </tr>
              )}
              {!loading && sortedRows.map(row => (
                <tr key={row.id} className={row.isAnomaly ? 'table-danger' : undefined}>
                  <td>
                    <span className={`badge ${row.status === 'open' ? 'bg-warning text-dark' : row.status === 'handled' ? 'bg-success' : 'bg-light text-dark'}`}>
                      {row.status === 'open' ? t('adminAdyenWebhookLogs.status.open') : row.status === 'handled' ? t('adminAdyenWebhookLogs.status.handled') : t('adminAdyenWebhookLogs.status.logged')}
                    </span>
                  </td>
                  <td>
                    {row.isAnomaly && row.anomalyKind ? (
                      <span className={`badge ${KIND_BADGE[row.anomalyKind] || 'bg-secondary'}`}>
                        {KIND_LABEL_KEY[row.anomalyKind] ? t(KIND_LABEL_KEY[row.anomalyKind]) : row.anomalyKind}
                      </span>
                    ) : (
                      <span className="badge bg-light text-dark">{t('adminAdyenWebhookLogs.anomalyNo')}</span>
                    )}
                  </td>
                  <td className="font-monospace small">{row.payloadType}</td>
                  <td className="font-monospace small">{row.reference || '—'}</td>
                  <td className="font-monospace small">{row.requestId || '—'}</td>
                  <td>
                    <small>{new Date(row.createdAt).toLocaleString('sv-SE')}</small>
                  </td>
                  <td className="text-end">
                    {row.status === 'open' ? (
                      <button
                        className="btn btn-sm btn-outline-success"
                        type="button"
                        onClick={() => markHandled(row)}
                        disabled={handlingId === row.id}
                      >
                        <i className="feather-check me-1"></i>
                        {t('adminAdyenWebhookLogs.handle')}
                      </button>
                    ) : (
                      <small className="text-muted">
                        {row.handledAt ? new Date(row.handledAt).toLocaleDateString('sv-SE') : t('adminAdyenWebhookLogs.status.handled')}
                      </small>
                    )}
                  </td>
                  <td className="font-monospace small">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-start text-decoration-none font-monospace"
                      onClick={() => setPayloadModalRow(row)}
                    >
                      {payloadPreview(row.rawPayload, 140)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {payloadModalRow && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.45)' }} tabIndex={-1}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">{t('adminAdyenWebhookLogs.modalTitle')}</h5>
                  <small className="text-muted">
                    {payloadModalRow.payloadType} · {new Date(payloadModalRow.createdAt).toLocaleString('sv-SE')}
                  </small>
                </div>
                <button type="button" className="btn-close" aria-label={t('adminAdyenWebhookLogs.close')} onClick={() => setPayloadModalRow(null)} />
              </div>
              <div className="modal-body">
                <pre className="bg-light border rounded p-3 mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {prettyPayload(payloadModalRow.rawPayload)}
                </pre>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPayloadModalRow(null)}>
                  {t('adminAdyenWebhookLogs.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
